import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { ARCHETYPE_TAGS, REPLICA_CODE_RE } from '../../community/constants.ts';
import { formatEvs, MONS } from '../mock/data.ts';
import type { PokemonSpec, SourceId, SpeciesId } from '../types.ts';
import { Editor, type EditorMember } from './builder/Editor.tsx';
import { serializeTeam } from './builder/serialize.ts';
import { CitationBadge } from './CitationBadge.tsx';
import { NatureGlyph } from './NatureGlyph.tsx';
import { Sprite } from './Sprite.tsx';
import { TypeChip } from './TypeChip.tsx';

// Mirror of MEGA_STONE_TO_SPECIES in src/calc/champions-megas.ts. Inlined
// here (not imported) because that module pulls in @pkmn/dex + Drizzle, which
// would bloat the web bundle for a constant map. Keep these in sync — when
// a new mega lands in champions-megas.ts, add it here too.
const MEGA_STONE_TO_DISPLAY: Record<string, string> = {
  froslassite: 'Froslass-Mega',
  glimmoranite: 'Glimmora-Mega',
  sneaselerite: 'Sneasler-Mega',
  whimsicottite: 'Whimsicott-Mega',
  scovillainite: 'Scovillain-Mega',
  golurkite: 'Golurk-Mega',
  garchompite: 'Garchomp-Mega',
  dragonitite: 'Dragonite-Mega',
  meganiumite: 'Meganium-Mega',
  delphoxite: 'Delphox-Mega',
  floettite: 'Floette-Mega',
  charizarditey: 'Charizard-Mega-Y',
  charizarditex: 'Charizard-Mega-X',
  aerodactylite: 'Aerodactyl-Mega',
  tyranitarite: 'Tyranitar-Mega',
  gengarite: 'Gengar-Mega',
  gardevoirite: 'Gardevoir-Mega',
  blastoisinite: 'Blastoise-Mega',
  scizorite: 'Scizor-Mega',
  venusaurite: 'Venusaur-Mega',
  kangaskhanite: 'Kangaskhan-Mega',
  lopunnite: 'Lopunny-Mega',
  aggronite: 'Aggron-Mega',
  absolite: 'Absol-Mega',
  abomasite: 'Abomasnow-Mega',
};

// Lowercase species id used for sprite lookup. "froslass" + "froslassite" →
// "froslass-mega" (matches Showdown's hyphenated gen5/dex sprite filename).
function effectiveSpeciesId(p: { species?: string; item?: string }): string {
  const speciesRaw = String(p.species ?? '').toLowerCase();
  if (!speciesRaw) return '';
  const itemKey = String(p.item ?? '')
    .toLowerCase()
    .replace(/\s+/g, '');
  const megaForm = MEGA_STONE_TO_DISPLAY[itemKey];
  if (megaForm && !/-mega(?:-[xy])?$/i.test(speciesRaw)) {
    return megaForm.toLowerCase();
  }
  return speciesRaw;
}

// Display name accounting for mega stones. "Froslass" + "froslassite" →
// "Froslass-Mega". Already-mega species names ("charizard-mega-y") are left
// as-is, just titlecased.
function speciesDisplayName(p: { species?: string; item?: string }): string {
  const speciesRaw = String(p.species ?? '');
  if (!speciesRaw) return '?';
  const itemKey = String(p.item ?? '')
    .toLowerCase()
    .replace(/\s+/g, '');
  const megaForm = MEGA_STONE_TO_DISPLAY[itemKey];
  if (megaForm && !/-mega(?:-[xy])?$/i.test(speciesRaw)) {
    return megaForm;
  }
  const mon = MONS[speciesRaw as SpeciesId];
  return mon?.name ?? speciesRaw;
}

export type TeamCardData = {
  creator?: string;
  date?: string;
  members: PokemonSpec[];
};

export function TeamCard({
  team,
  title,
  source,
  sourceMeta,
  density = 'compact',
  defaultExpanded = false,
  showdownExport,
  regulationId,
}: {
  team: TeamCardData | PokemonSpec[];
  title?: string;
  source?: SourceId;
  sourceMeta?: string;
  density?: 'compact' | 'expanded';
  defaultExpanded?: boolean;
  // When present, the card renders Save + Export buttons. The export string
  // is the canonical Showdown text the agent passed to validate_team — used
  // verbatim for copy-to-clipboard and POST-to-API. Absent for tournament
  // teams (semantic_search_teams results), which we don't let users "save"
  // because they're not the user's own roster.
  showdownExport?: string;
  regulationId?: string;
}) {
  const originalMembers = Array.isArray(team) ? team : team.members;
  const creator = !Array.isArray(team) ? team.creator : undefined;
  const date = !Array.isArray(team) ? team.date : undefined;
  const [expanded, setExpanded] = useState(defaultExpanded || density === 'expanded');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<{ id: number; name: string } | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Edit mode owns its own working copy of the team. The parent's
  // `members` prop is only used as the SEED on enter-edit; subsequent
  // edits live in this state and serialize back to a Showdown export
  // on save. Exiting without save reverts to originalMembers.
  const [editing, setEditing] = useState(false);
  const [editedMembers, setEditedMembers] = useState<EditorMember[] | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const members: PokemonSpec[] | EditorMember[] =
    editing && editedMembers ? editedMembers : originalMembers;

  async function onSave() {
    // When editing, serialize the working copy; otherwise re-save the
    // original Showdown export (the existing as-pasted behavior).
    const exportText = editing && editedMembers ? serializeTeam(editedMembers) : showdownExport;
    if (!exportText) return;
    setSaving(true);
    setSaveErr(null);
    try {
      const name = derivedTeamName(members);
      const res = await fetch('/api/teams', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          showdown_export: exportText,
          regulation_id: regulationId,
          name,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.ok === false) {
        setSaveErr(
          body.error === 'unauthorized' ? 'sign in to save' : (body.error ?? 'save failed'),
        );
      } else {
        setSaved({ id: body.team.id, name: body.team.name });
        // Exit edit mode on successful save; the edited members are now
        // the committed shape and the parent will re-render with the new
        // export string on the next validate_team round-trip.
        setEditing(false);
      }
    } catch {
      setSaveErr('network error');
    } finally {
      setSaving(false);
    }
  }

  function startEditing() {
    // Seed the working copy from the current members. EditorMember is a
    // relaxed superset of PokemonSpec so a direct shallow copy is safe.
    setEditedMembers(originalMembers.map((m) => ({ ...m })) as EditorMember[]);
    setEditing(true);
    setExpanded(true); // edit mode always expanded; hide compact toggle
  }

  function cancelEditing() {
    setEditing(false);
    setEditedMembers(null);
    setSaveErr(null);
  }

  function updateMemberAt(i: number, next: EditorMember) {
    setEditedMembers((prev) => {
      if (!prev) return prev;
      const copy = [...prev];
      copy[i] = next;
      return copy;
    });
  }

  async function onExport() {
    if (!showdownExport) return;
    try {
      await navigator.clipboard.writeText(showdownExport);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // No-op — clipboard API can fail in non-secure contexts; user sees no
      // change and can fall back to the agent re-printing the export.
    }
  }

  return (
    <div className="vb-card" style={{ overflow: 'hidden' }}>
      {(title || source || creator) && (
        <div className="vb-card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {title && (
              <span
                className="caps"
                style={{ color: 'var(--fg-3)', fontSize: 10, letterSpacing: '0.08em' }}
              >
                {title}
              </span>
            )}
            {creator && (
              <span style={{ color: 'var(--fg-1)', fontSize: 12, fontWeight: 500 }}>{creator}</span>
            )}
            {date && (
              <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 11 }}>
                {date}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {source && <CitationBadge source={source} suffix={sourceMeta} />}
            {showdownExport && !editing && (
              <>
                <button
                  type="button"
                  onClick={onExport}
                  className="caps mono"
                  style={chipBtnStyle(copied ? 'var(--accent-1)' : undefined)}
                  title="Copy Showdown export to clipboard"
                >
                  {copied ? 'copied' : 'export'}
                </button>
                <button
                  type="button"
                  onClick={startEditing}
                  className="caps mono"
                  style={chipBtnStyle()}
                  title="Edit this team in the builder"
                >
                  edit
                </button>
                <button
                  type="button"
                  onClick={onSave}
                  disabled={saving || !!saved}
                  className="caps mono"
                  style={chipBtnStyle(saved ? 'var(--accent-1)' : undefined)}
                  title={saved ? `Saved as "${saved.name}"` : 'Save this team to your account'}
                >
                  {saved ? 'saved' : saving ? 'saving…' : 'save'}
                </button>
                <button
                  type="button"
                  onClick={() => setPublishOpen(true)}
                  className="caps mono"
                  style={chipBtnStyle()}
                  title="Publish this team to the community feed"
                >
                  publish
                </button>
                {saveErr && (
                  <span
                    className="mono"
                    style={{ color: 'var(--neg)', fontSize: 9.5 }}
                    title={saveErr}
                  >
                    {saveErr}
                  </span>
                )}
              </>
            )}
            {editing && (
              <>
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="caps mono"
                  style={chipBtnStyle()}
                  title="Discard edits"
                >
                  cancel
                </button>
                <button
                  type="button"
                  onClick={onSave}
                  disabled={saving}
                  className="caps mono"
                  style={chipBtnStyle('var(--accent-1)')}
                  title="Save edited team to your account"
                >
                  {saving ? 'saving…' : 'save changes'}
                </button>
                {saveErr && (
                  <span
                    className="mono"
                    style={{ color: 'var(--neg)', fontSize: 9.5 }}
                    title={saveErr}
                  >
                    {saveErr}
                  </span>
                )}
              </>
            )}
            {!editing && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="caps mono"
                style={chipBtnStyle()}
              >
                {expanded ? 'compact' : 'expand'}
              </button>
            )}
          </div>
        </div>
      )}

      {editing && editedMembers ? (
        <EditMode
          members={editedMembers}
          onMemberChange={updateMemberAt}
          regulationId={regulationId}
        />
      ) : !expanded ? (
        <CompactTeam members={members as PokemonSpec[]} />
      ) : (
        <ExpandedTeam members={members as PokemonSpec[]} />
      )}
      {publishOpen && showdownExport && (
        <PublishDialog
          defaultName={derivedTeamName(members as PokemonSpec[])}
          showdownExport={
            editing && editedMembers ? serializeTeam(editedMembers) : showdownExport
          }
          regulationId={regulationId}
          onClose={() => setPublishOpen(false)}
        />
      )}
    </div>
  );
}

// Stack of Editor components, one per slot. The parent owns the array;
// each Editor receives a single member and calls back with the updated
// member.
function EditMode({
  members,
  onMemberChange,
  regulationId,
}: {
  members: EditorMember[];
  onMemberChange: (i: number, next: EditorMember) => void;
  regulationId?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 12 }}>
      {members.map((m, i) => (
        <Editor
          // biome-ignore lint/suspicious/noArrayIndexKey: slot index IS the identity in edit mode (species can change)
          key={i}
          member={m}
          onChange={(next) => onMemberChange(i, next)}
          slotLabel={`slot ${i + 1}`}
          regulationId={regulationId}
        />
      ))}
    </div>
  );
}

function CompactTeam({ members }: { members: PokemonSpec[] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(6, 1fr)',
        gap: 4,
        padding: 8,
      }}
    >
      {members.map((p, i) => {
        return (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: same species can appear twice if Species Clause is off
            key={`${p.species}-${i}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              padding: 4,
            }}
          >
            <Sprite species={effectiveSpeciesId(p) as SpeciesId} size={48} />
            <span
              className="mono"
              style={{
                color: 'var(--fg-2)',
                fontSize: 9.5,
                letterSpacing: '0.04em',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
            >
              {speciesDisplayName(p)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ExpandedTeam({ members }: { members: PokemonSpec[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {members.map((p, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: same species can appear twice if Species Clause is off
        <ExpandedRow key={`${p.species}-${i}`} p={p} last={i === members.length - 1} />
      ))}
    </div>
  );
}

function ExpandedRow({ p, last }: { p: PokemonSpec; last: boolean }) {
  const mon = MONS[p.species as SpeciesId];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '64px 1fr',
        gap: 12,
        padding: 10,
        borderBottom: last ? 'none' : '1px solid var(--line-1)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        <Sprite species={effectiveSpeciesId(p) as SpeciesId} size={56} />
        <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          {mon?.types.map((t) => (
            <TypeChip key={t} type={t} size="sm" />
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: 'var(--fg-1)' }}>{speciesDisplayName(p)}</span>
          <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 11 }}>
            @ {p.item}
          </span>
          <NatureGlyph nature={p.nature} />
          {p.teraType && (
            <span
              className="caps mono"
              style={{
                fontSize: 9,
                color: 'var(--accent-2)',
                background: 'var(--accent-bg)',
                border: '1px solid var(--accent-line)',
                borderRadius: 2,
                padding: '1px 5px',
                letterSpacing: '0.08em',
              }}
            >
              tera {p.teraType}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--fg-2)' }}>
          <span>
            <span className="caps" style={{ color: 'var(--fg-4)', marginRight: 4 }}>
              abil
            </span>
            {p.ability}
          </span>
        </div>
        <div className="mono" style={{ color: 'var(--fg-3)', fontSize: 11 }}>
          {formatEvs(p.evs)}
        </div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {p.moves.map((m) => (
            <span
              key={m}
              style={{
                fontSize: 11,
                padding: '2px 7px',
                background: 'var(--bg-2)',
                border: '1px solid var(--line-1)',
                borderRadius: 3,
                color: 'var(--fg-1)',
              }}
            >
              {m}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Shared header-button styling. Accent override is used to flag the "copied"
// / "saved" transient states without flashing a different control.
function chipBtnStyle(accent?: string): React.CSSProperties {
  return {
    color: accent ?? 'var(--fg-3)',
    fontSize: 9.5,
    letterSpacing: '0.08em',
    padding: '2px 6px',
    border: `1px solid ${accent ?? 'var(--line-1)'}`,
    borderRadius: 3,
    background: 'var(--bg-2)',
    cursor: 'pointer',
  };
}

// Build a sensible default team name from the first 2-3 species. The user
// can rename later via an account-page UI (not built yet); for now this
// gives "Sneasler / Charizard / Garchomp" rather than "Untitled team".
function derivedTeamName(members: PokemonSpec[]): string {
  const names = members.slice(0, 3).map((m) => {
    const slug = (m.species ?? '').toString();
    if (!slug) return '?';
    // "charizard-mega-y" → "Charizard"
    const base = slug.split('-')[0];
    return base.charAt(0).toUpperCase() + base.slice(1);
  });
  return names.join(' / ') || 'Untitled team';
}

function PublishDialog({
  defaultName,
  showdownExport,
  regulationId,
  onClose,
}: {
  defaultName: string;
  showdownExport: string;
  regulationId?: string;
  onClose: () => void;
}) {
  const [name, setName] = useState(defaultName);
  const [blurb, setBlurb] = useState('');
  const [replicaCode, setReplicaCode] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [published, setPublished] = useState<{ id: number } | null>(null);
  // Auth-gate the dialog: don't waste the user's time filling fields they'll
  // lose on the unauthorized response. null = still checking.
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  // Focus the name input on open (a11y); restore focus on close.
  const nameRef = useRef<HTMLInputElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    fetch('/api/auth/me')
      .then((r) => r.json())
      .then((j) => setSignedIn(j?.user != null))
      .catch(() => setSignedIn(false));
    return () => {
      previousFocusRef.current?.focus?.();
    };
  }, []);

  useEffect(() => {
    if (signedIn) nameRef.current?.focus();
  }, [signedIn]);

  // Escape at document level — onKeyDown on the backdrop can be swallowed
  // when focus is inside an input that stops propagation.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const trimmedCode = replicaCode.trim().toUpperCase();
  const codeShapeOk = trimmedCode.length === 0 || REPLICA_CODE_RE.test(trimmedCode);
  const canSubmit = name.trim().length > 0 && codeShapeOk && !busy;

  function toggleTag(tag: string) {
    setTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : prev.length >= 3 ? prev : [...prev, tag],
    );
  }

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch('/api/community/teams', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim(),
          blurb: blurb.trim() || undefined,
          replica_code: trimmedCode || undefined,
          showdown_export: showdownExport,
          regulation_id: regulationId,
          archetype_tags: tags,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        team?: { id: number };
        error?: string;
        message?: string;
      };
      if (!res.ok || body.ok === false) {
        if (body.error === 'unauthorized') {
          setErr('Sign in to publish a team.');
        } else {
          setErr(body.message ?? body.error ?? 'publish failed');
        }
        return;
      }
      if (body.team) setPublished({ id: body.team.id });
    } catch {
      setErr('network error');
    } finally {
      setBusy(false);
    }
  }

  function gotoTeam() {
    if (!published) return;
    window.history.pushState({}, '', `/community/${published.id}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="publish-dialog-title"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-1)',
          border: '1px solid var(--line-1)',
          borderRadius: 6,
          width: 'min(520px, 100%)',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2
            id="publish-dialog-title"
            style={{
              margin: 0,
              fontFamily: 'var(--font-serif)',
              fontSize: 20,
              fontWeight: 400,
              color: 'var(--fg-1)',
            }}
          >
            {published ? 'Your team is live.' : 'Publish to community'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--fg-3)',
              fontSize: 20,
              cursor: 'pointer',
              padding: 0,
              width: 28,
              height: 28,
            }}
          >
            ✕
          </button>
        </div>

        {signedIn === null ? (
          <span className="caps mono" style={{ color: 'var(--fg-4)', fontSize: 10 }}>
            checking sign-in…
          </span>
        ) : signedIn === false ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ margin: 0, color: 'var(--fg-2)', fontSize: 13.5 }}>
              You need to sign in to publish a team to the community feed.
            </p>
            <a
              href="/account"
              className="caps mono"
              style={{
                alignSelf: 'flex-start',
                fontSize: 10,
                padding: '8px 14px',
                background: 'var(--accent-1)',
                color: 'var(--bg-0)',
                border: '1px solid var(--accent-line)',
                borderRadius: 3,
                letterSpacing: '0.08em',
                textDecoration: 'none',
              }}
            >
              sign in →
            </a>
          </div>
        ) : published ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, color: 'var(--fg-2)', fontSize: 13.5 }}>
              Your team is live on the community feed. Other players can rent and try it; they'll
              press <em>works</em> or <em>broken</em> after testing the code in Champions.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={gotoTeam}
                className="caps mono"
                style={{
                  fontSize: 10,
                  padding: '8px 14px',
                  background: 'var(--accent-1)',
                  color: 'var(--bg-0)',
                  border: '1px solid var(--accent-line)',
                  borderRadius: 3,
                  letterSpacing: '0.08em',
                  cursor: 'pointer',
                }}
              >
                view in community →
              </button>
              <button
                type="button"
                onClick={onClose}
                className="caps mono"
                style={{
                  fontSize: 10,
                  padding: '8px 14px',
                  background: 'transparent',
                  color: 'var(--fg-2)',
                  border: '1px solid var(--line-1)',
                  borderRadius: 3,
                  letterSpacing: '0.08em',
                  cursor: 'pointer',
                }}
              >
                close
              </button>
            </div>
          </div>
        ) : (
          <>
            <p style={{ margin: 0, color: 'var(--fg-3)', fontSize: 12.5, lineHeight: 1.5 }}>
              Your team will appear in the public feed. Other players can press <em>works</em> or{' '}
              <em>broken</em> after trying the rental code in Champions — that's the trust signal.
            </p>

            <Field label="Team name (required)">
              <input
                ref={nameRef}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={200}
                style={inputStyle()}
              />
            </Field>

            <Field label="Blurb (optional, 500 chars)">
              <textarea
                value={blurb}
                onChange={(e) => setBlurb(e.target.value.slice(0, 500))}
                rows={3}
                style={{ ...inputStyle(), resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }}
                placeholder="One-liner about how this team plays."
              />
              <span style={{ color: 'var(--fg-4)', fontSize: 10 }}>{blurb.length}/500</span>
            </Field>

            <Field label="Rental code (optional, XXXX-XXXX-XX)">
              <input
                type="text"
                value={replicaCode}
                onChange={(e) => setReplicaCode(e.target.value.toUpperCase())}
                placeholder="ABCD-EFGH-12"
                className="mono"
                style={{ ...inputStyle(), letterSpacing: '0.04em' }}
              />
              {!codeShapeOk && (
                <span style={{ color: 'var(--neg)', fontSize: 11 }}>
                  Should look like ABCD-EFGH-12 (10 chars, hyphenated).
                </span>
              )}
            </Field>

            <Field label={`Archetype tags (up to 3 — ${tags.length}/3 selected)`}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {ARCHETYPE_TAGS.map((tag) => {
                  const active = tags.includes(tag);
                  const atLimit = !active && tags.length >= 3;
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      disabled={atLimit}
                      className="caps mono"
                      style={{
                        fontSize: 10,
                        padding: '4px 9px',
                        background: active ? 'var(--accent-bg)' : 'transparent',
                        color: active ? 'var(--accent-2)' : atLimit ? 'var(--fg-4)' : 'var(--fg-2)',
                        border: `1px solid ${active ? 'var(--accent-line)' : 'var(--line-1)'}`,
                        borderRadius: 3,
                        letterSpacing: '0.08em',
                        cursor: atLimit ? 'not-allowed' : 'pointer',
                        opacity: atLimit ? 0.5 : 1,
                      }}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </Field>

            {err && <span style={{ color: 'var(--neg)', fontSize: 12 }}>{err}</span>}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                className="caps mono"
                style={{
                  fontSize: 10,
                  padding: '8px 14px',
                  background: 'transparent',
                  color: 'var(--fg-2)',
                  border: '1px solid var(--line-1)',
                  borderRadius: 3,
                  letterSpacing: '0.08em',
                  cursor: 'pointer',
                }}
              >
                cancel
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={!canSubmit}
                className="caps mono"
                style={{
                  fontSize: 10,
                  padding: '8px 14px',
                  background: canSubmit ? 'var(--accent-1)' : 'var(--bg-2)',
                  color: canSubmit ? 'var(--bg-0)' : 'var(--fg-4)',
                  border: `1px solid ${canSubmit ? 'var(--accent-line)' : 'var(--line-1)'}`,
                  borderRadius: 3,
                  letterSpacing: '0.08em',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                }}
              >
                {busy ? 'publishing…' : 'publish team'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        className="caps mono"
        style={{ fontSize: 9.5, color: 'var(--fg-4)', letterSpacing: '0.1em' }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    padding: '6px 10px',
    background: 'var(--bg-0)',
    border: '1px solid var(--line-1)',
    borderRadius: 3,
    color: 'var(--fg-1)',
    fontSize: 13,
  };
}
